/**
 * Logistics Service Manager
 * Handles adding/removing logistics service product from cart
 */

import {
  LOGISTICS_VARIANT_ID,
  ASSEMBLY_VARIANT_ID,
  MIN_CART_TOTAL_THRESHOLD,
  getLogisticsEligibleProductItems,
  isCartZubehoerOnly,
} from './logistics-utils.js';

export {
  LOGISTICS_VARIANT_ID,
  ASSEMBLY_VARIANT_ID,
  MIN_CART_TOTAL_THRESHOLD,
  isCartZubehoerOnly,
};

// Cart attribute to store the applied discount code when it's one of the tracked codes
const APPLIED_DISCOUNT_CODE_ATTRIBUTE_KEY = 'applied_discount_code';
// When one of these codes is applied, we set the attribute to that code (e.g. "FLEX45"). Otherwise we clear it.
const TRACKED_DISCOUNT_CODES = ['FLEX45']; // Add any codes you want to track here (case-insensitive)

// Single global singleton — never replace this object (duplicate module loads must share it)
function getLogisticsLock() {
  const g = typeof globalThis !== 'undefined' ? globalThis : window;
  if (!g.__flexcubeLogisticsLock) {
    g.__flexcubeLogisticsLock = {
      managing: false,
      pending: false,
      pendingTimer: null,
      initialized: false,
      subscribed: false,
      recentlyAddedAt: 0,
      handlingMarketChange: false,
      chain: Promise.resolve(),
      mutationChain: Promise.resolve(),
      unsubscribeCart: null,
      onFocus: null,
      onVisibility: null,
      pollInterval: null,
      slowPollInterval: null,
    };
  }
  const lock = g.__flexcubeLogisticsLock;
  if (!lock.chain) lock.chain = Promise.resolve();
  if (!lock.mutationChain) lock.mutationChain = Promise.resolve();
  return lock;
}

const logisticsLock = getLogisticsLock();

function clearPendingLogisticsCheck() {
  logisticsLock.pending = false;
  if (logisticsLock.pendingTimer) {
    clearTimeout(logisticsLock.pendingTimer);
    logisticsLock.pendingTimer = null;
  }
}

function schedulePendingLogisticsCheck(delayMs = 300) {
  logisticsLock.pending = true;
  if (logisticsLock.pendingTimer) clearTimeout(logisticsLock.pendingTimer);
  logisticsLock.pendingTimer = setTimeout(() => {
    logisticsLock.pendingTimer = null;
    if (!logisticsLock.pending) return;
    logisticsLock.pending = false;
    manageLogisticsProduct(null, null).catch((error) => {
      console.error('[Logistics Manager] Error on queued logistics check:', error);
    });
  }, delayMs);
}

function cartHasLogistics(items) {
  return Array.isArray(items) && items.some((item) => String(item.variant_id) === LOGISTICS_VARIANT_ID);
}

function getLogisticsLines(items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => String(item.variant_id) === LOGISTICS_VARIANT_ID);
}

async function normalizeLogisticsQuantity(cartData, sectionsToUpdate) {
  const logisticsLines = getLogisticsLines(cartData?.items);
  const logisticsQty = logisticsLines.reduce((sum, item) => sum + (item.quantity || 0), 0);
  if (logisticsQty <= 1 && logisticsLines.length <= 1) {
    return cartData;
  }

  console.log('[Logistics Manager] Duplicate logistics detected — normalizing to quantity 1');
  const cartChangeUrl = typeof theme !== 'undefined' && theme.routes?.cart_change_url
    ? theme.routes.cart_change_url
    : '/cart/change.js';
  const sectionsParam = sectionsToUpdate ? Array.from(sectionsToUpdate).join(',') : '';
  const primary = logisticsLines[0];

  let lastResult = null;
  const changeRes = await fetch(cartChangeUrl, fetchConfig('json', {
    body: JSON.stringify({
      id: primary.key,
      quantity: 1,
      sections: sectionsParam,
      sections_url: window.location.pathname,
    }),
  }));
  if (changeRes.ok) {
    lastResult = await changeRes.json().catch(() => null);
  }

  for (let i = 1; i < logisticsLines.length; i++) {
    const res = await fetch(cartChangeUrl, fetchConfig('json', {
      body: JSON.stringify({
        id: logisticsLines[i].key,
        quantity: 0,
        sections: sectionsParam,
        sections_url: window.location.pathname,
      }),
    }));
    if (res.ok) {
      lastResult = await res.json().catch(() => lastResult);
    }
  }

  if (lastResult?.sections && sectionsToUpdate?.size > 0) {
    updateCartSections(lastResult.sections, sectionsToUpdate);
  }

  const normalized = await fetchFreshCartData();
  return normalized || cartData;
}

/**
 * Removes logistics fee notice/summary nodes from a cart container.
 * @param {Element|null} container
 */
function removeLogisticsSummaryFromContainer(container) {
  if (!container) return;
  container.querySelectorAll(
    '.cart__logistics-notice, .cart__logistics-fees, [data-logistics-notice], [data-service="logistics"]'
  ).forEach((node) => node.remove());
}

/**
 * Strips logistics UI from all cart drawer/page containers on the page.
 */
export function stripLogisticsSummaryFromAllContainers() {
  document.querySelectorAll('cart-drawer, main-cart, .cart-service-products-summary, [data-service-summary]').forEach((container) => {
    removeLogisticsSummaryFromContainer(container);
  });
}

/** Schedule delayed strips so stale section re-renders don't bring logistics UI back. */
function scheduleDelayedLogisticsStrips(container) {
  if (!container) return;
  [400, 750, 1100].forEach((ms) => {
    setTimeout(() => removeLogisticsSummaryFromContainer(container), ms);
  });
}

async function fetchFreshCartData() {
  const base = (typeof theme !== 'undefined' && theme.routes?.cart_url) ? theme.routes.cart_url : '/cart';
  const cartUrl = base.endsWith('.js') ? base : base + '.js';
  // Cache-bust: stale GET /cart.js after add/remove causes false "no logistics" → double add
  const separator = cartUrl.includes('?') ? '&' : '?';
  const response = await fetch(`${cartUrl}${separator}_=${Date.now()}`, {
    ...fetchConfig('json', { method: 'GET' }),
    cache: 'no-store',
  });
  if (!response.ok) return null;
  return response.json();
}

/**
 * Returns the applied discount code string if it's one of the tracked codes, else null
 * Cart API can return discount codes as discount_codes[] (each with .code) or
 * cart_level_discount_applications[] (each with .type, .title or .code).
 * @param {any} cartData - Cart object from API (cart.js)
 * @returns {string|null} e.g. "FLEX45" or null
 */
function getAppliedTrackedDiscountCode(cartData) {
  const tracked = Array.isArray(TRACKED_DISCOUNT_CODES) ? TRACKED_DISCOUNT_CODES : [TRACKED_DISCOUNT_CODES];
  const trackedUpper = tracked.map((c) => String(c).trim().toUpperCase()).filter(Boolean);
  if (trackedUpper.length === 0) return null;

  // 1) Cart update response uses discount_codes: [{ code, applicable }, ...]
  const discountCodes = cartData?.discount_codes;
  if (Array.isArray(discountCodes)) {
    for (const d of discountCodes) {
      const code = (d?.code ?? '').trim();
      if (!code) continue;
      if (trackedUpper.includes(code.toUpperCase())) return code;
    }
  }

  // 2) Full cart (GET /cart.js) may use cart_level_discount_applications: [{ type, title }, ...]
  const applications = cartData?.discount_applications || cartData?.cart_level_discount_applications || [];
  for (const app of applications) {
    if (app.type !== 'discount_code') continue;
    const code = (app.title || app.code || '').trim();
    if (code && trackedUpper.includes(code.toUpperCase())) return code;
  }

  return null;
}

/**
 * Updates cart attribute applied_discount_code: set to the code when a tracked coupon is applied, clear when not.
 * Fetches fresh cart (GET /cart.js) so we always see current discount codes regardless of which event payload we got.
 * @param {any} [cartData] - Optional cart from event; we fetch fresh cart to read discount codes reliably
 * @returns {Promise<void>}
 */
async function setAppliedDiscountCodeAttribute(cartData) {
  const key = APPLIED_DISCOUNT_CODE_ATTRIBUTE_KEY;
  const base = (typeof theme !== 'undefined' && theme.routes?.cart_url) ? theme.routes.cart_url : '/cart';
  const cartUrl = base.endsWith('.js') ? base : base + '.js';
  let dataToUse = cartData;
  if (!dataToUse?.discount_codes?.length && !dataToUse?.cart_level_discount_applications?.length && !dataToUse?.discount_applications?.length) {
    try {
      const res = await fetch(cartUrl, fetchConfig('json', { method: 'GET' }));
      if (res.ok) dataToUse = await res.json();
    } catch (e) {
      console.warn('[Logistics Manager] Could not fetch cart for discount code check:', e);
    }
  }
  const value = getAppliedTrackedDiscountCode(dataToUse || cartData);
  const cartUpdateUrl = typeof theme !== 'undefined' && theme.routes?.cart_update_url ? theme.routes.cart_update_url : '/cart/update.js';
  const body = JSON.stringify({
    attributes: { [key]: value != null ? value : '' }
  });
  try {
    const response = await fetch(cartUpdateUrl, fetchConfig('json', { body }));
    if (!response.ok) {
      console.warn('[Logistics Manager] Failed to set cart attribute', key, await response.text());
      return;
    }
    if (value) {
      console.log('[Logistics Manager] Cart attribute set:', key, '=', value);
    } else {
      console.log('[Logistics Manager] Cart attribute cleared:', key);
    }
  } catch (error) {
    console.warn('[Logistics Manager] Error setting cart attribute:', error);
  }
}

/**
 * Gets fetch config utility
 */
function fetchConfig(type = 'json', options = {}) {
  if (typeof theme !== 'undefined' && theme.utils?.fetchConfig) {
    const config = theme.utils.fetchConfig(type, options.method || 'POST');
    return { ...config, ...options };
  }
  return {
    method: options.method || 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` },
    ...options
  };
}

/**
 * Checks if logistics fees should be disabled for the current market
 * @returns {boolean} True if logistics fees should be disabled
 */
function isLogisticsDisabled() {
  // Primary source: theme.localization (most reliable, set by Liquid template)
  const themeCountryCode = typeof theme !== 'undefined' && theme.localization?.country?.iso_code;
  const themeMarketHandle = typeof theme !== 'undefined' && theme.localization?.market?.handle;
  
  // Check if country code indicates Switzerland (primary indicator)
  const isSwitzerland = themeCountryCode === 'CH' || themeCountryCode === 'ch';
  
  // If country code indicates Switzerland, logistics is disabled
  if (isSwitzerland) {
    return true;
  }
  
  // Secondary checks: market handle, URL, and DOM attributes (fallbacks)
  const pathname = window.location.pathname;
  const urlHasChMarket = pathname.startsWith('/ch/') || pathname === '/ch' || pathname.startsWith('/ch?');
  const windowMarket = window.Shopify?.market || window.market;
  const windowMarketHandle = windowMarket?.handle;
  const bodyMarket = document.body?.getAttribute('data-market');
  const htmlMarket = document.documentElement?.getAttribute('data-market');
  
  // Determine if we're in the 'ch' market (fallback checks)
  return themeMarketHandle === 'ch' ||
         urlHasChMarket || 
         windowMarketHandle === 'ch' ||
         bodyMarket === 'ch' ||
         htmlMarket === 'ch';
}

/**
 * Collects sections that need to be updated from cart elements on page
 * @returns {Set<string>} Set of section IDs to update
 */
function collectSectionsToUpdate() {
  const sectionsToUpdate = new Set();
  
  // Collect from main-cart elements
  document.querySelectorAll('main-cart').forEach((element) => {
    const sectionId = element.getAttribute('data-section-id');
    if (sectionId) {
      sectionsToUpdate.add(sectionId);
    }
  });
  
  // Collect from cart-drawer elements
  document.querySelectorAll('cart-drawer').forEach((element) => {
    const sectionId = element.getAttribute('data-section-id');
    if (sectionId) {
      sectionsToUpdate.add(sectionId);
    }
  });
  
  // Trigger cart:bundled-sections event to let other components add their sections
  const event = new CustomEvent('cart:bundled-sections', {
    bubbles: true,
    detail: { sections: Array.from(sectionsToUpdate) }
  });
  document.documentElement.dispatchEvent(event);
  
  return new Set(event.detail.sections);
}

/**
 * Morphs/updates a section with new HTML content
 * @param {string} sectionId - Section ID to update
 * @param {string} html - New HTML content
 */
function morphSection(sectionId, html, options = {}) {
  if (!html) return;
  const stripLogisticsSummary = !!options.stripLogisticsSummary;
  
  try {
    const sectionToRender = new DOMParser().parseFromString(html, 'text/html');
    const miniCartId = `MiniCart-${sectionId}`;
    const mainCartId = `MainCart-${sectionId}`;
    
    const applyMorph = (target, updatedElement, onDone) => {
      // Prevent re-executing module scripts embedded in cart sections (e.g. assembly component)
      updatedElement.querySelectorAll('script').forEach((script) => script.remove());
      import('./cart-service-filter.js').then(({ filterServiceProductsFromCart }) => {
        filterServiceProductsFromCart(updatedElement);
        target.innerHTML = updatedElement.innerHTML;
        if (stripLogisticsSummary) removeLogisticsSummaryFromContainer(target);
        onDone?.();
      }).catch(() => {
        target.innerHTML = updatedElement.innerHTML;
        if (stripLogisticsSummary) removeLogisticsSummaryFromContainer(target);
        onDone?.();
      });
    };
    
    // Update MiniCart (cart drawer) - use getElementById for section IDs like template--xxx__main
    const miniCart = document.getElementById(miniCartId);
    if (miniCart) {
      const updatedElement = sectionToRender.getElementById(miniCartId);
      if (updatedElement) {
        applyMorph(miniCart, updatedElement, () => {
          if (stripLogisticsSummary) scheduleDelayedLogisticsStrips(miniCart);
        });
      }
    }
    
    // Update MainCart (cart page) - use getElementById for section IDs like template--xxx__main
    const mainCart = document.getElementById(mainCartId);
    if (mainCart) {
      const updatedElement = sectionToRender.getElementById(mainCartId);
      if (updatedElement) {
        const scrollTop = window.scrollY;
        applyMorph(mainCart, updatedElement, () => {
          if (stripLogisticsSummary) scheduleDelayedLogisticsStrips(mainCart);
          requestAnimationFrame(() => {
            setTimeout(() => {
              const newCartItems = mainCart.querySelector('cart-items');
              if (newCartItems) {
                document.dispatchEvent(new CustomEvent('cart:updated', {
                  detail: { source: 'logistics-service' }
                }));
              }
              requestIdleCallback(() => {
                window.scrollTo({ top: scrollTop, behavior: 'instant' });
              });
            }, 50);
          });
        });
      } else {
        // Empty cart: section HTML has no MainCart (it's inside {% if cart != empty %} in Liquid).
        // Remove main cart block so logistics/montage summary disappear and empty state shows.
        const cartWrapper = mainCart.closest('.cart');
        if (cartWrapper) cartWrapper.classList.add('is-empty');
        mainCart.remove();
      }
    }
  } catch (error) {
    console.error(`[Logistics Manager] Failed to morph section ${sectionId}:`, error);
  }
}

/**
 * Updates cart sections after logistics product changes
 * The Liquid template automatically re-renders service products summary when sections update
 * @param {Record<string, string>} sections - Section HTML to update
 * @param {Set<string>} sectionsToUpdate - Sections that need updating
 */
function updateCartSections(sectionsOrResult, sectionsToUpdate, options = {}) {
  const hasResult = sectionsOrResult && typeof sectionsOrResult === 'object' && 'sections' in sectionsOrResult;
  const sections = hasResult ? sectionsOrResult.sections : sectionsOrResult;
  const stripLogisticsSummary = hasResult ? !!sectionsOrResult.stripLogisticsSummary : !!options.stripLogisticsSummary;
  if (!sections || typeof sections !== 'object') {
    return;
  }
  
  sectionsToUpdate.forEach((sectionId) => {
    if (sections[sectionId]) {
      morphSection(sectionId, sections[sectionId], { stripLogisticsSummary });
    }
  });
}

/**
 * Fetches cart section HTML via Section Rendering API (GET /cart?sections=...).
 * Use when cart has no line items (no-op change not possible). Otherwise prefer fetchCartSectionsViaBundledRendering.
 * @param {Set<string>} [sectionsToUpdate] - Section IDs to fetch; defaults to collectSectionsToUpdate()
 * @returns {Promise<Record<string, string>>} Fetched sections object, or {}
 */
async function fetchCartSectionsAndUpdate(sectionsToUpdate = null) {
  if (!sectionsToUpdate) sectionsToUpdate = collectSectionsToUpdate();
  if (!sectionsToUpdate || sectionsToUpdate.size === 0) return {};
  const cartPageUrl = (typeof theme !== 'undefined' && theme.routes?.cart_url) ? theme.routes.cart_url : '/cart';
  const sectionsParam = Array.from(sectionsToUpdate).join(',');
  const url = `${cartPageUrl}${cartPageUrl.includes('?') ? '&' : '?'}sections=${encodeURIComponent(sectionsParam)}`;
  try {
    const response = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
    if (!response.ok) return {};
    const data = await response.json();
    const sections = typeof data === 'object' && data !== null ? data : {};
    if (Object.keys(sections).length > 0) {
      updateCartSections(sections, sectionsToUpdate);
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
      return sections;
    }
  } catch (error) {
    console.warn('[Logistics Manager] Failed to fetch cart sections for refresh:', error);
  }
  return {};
}

/**
 * Fetches cart section HTML using the same method as add/remove logistics: bundled section rendering
 * via POST /cart/change.js with sections + sections_url. This ensures /cart page and drawer both
 * get the same section response format and update correctly (unlike GET /cart?sections= which can
 * differ on cart page). Uses a no-op change (one line item quantity unchanged) when cart has items.
 * @param {any} cartData - Cart object (from event.cart or /cart.js); used to pick a line for no-op change
 * @param {Set<string>} [sectionsToUpdate] - Section IDs to request; defaults to collectSectionsToUpdate()
 * @returns {Promise<Record<string, string>>} Fetched sections object, or {}
 */
async function fetchCartSectionsViaBundledRendering(cartData, sectionsToUpdate = null) {
  if (!sectionsToUpdate) sectionsToUpdate = collectSectionsToUpdate();
  if (!sectionsToUpdate || sectionsToUpdate.size === 0) return {};
  const sectionsParam = Array.from(sectionsToUpdate).join(',');
  const sectionsUrl = window.location.pathname || '/cart';
  const cartChangeUrl = typeof theme !== 'undefined' && theme.routes?.cart_change_url ? theme.routes.cart_change_url : '/cart/change.js';

  // When cart has items, use a no-op cart/change (same quantity) so response includes sections (same as add/remove logistics)
  const items = cartData?.items && Array.isArray(cartData.items) ? cartData.items : [];
  if (items.length > 0) {
    const first = items[0];
    const body = JSON.stringify({
      id: first.key,
      quantity: first.quantity,
      sections: sectionsParam,
      sections_url: sectionsUrl,
    });
    try {
      const response = await fetch(cartChangeUrl, fetchConfig('json', { body }));
      if (!response.ok) return await fetchCartSectionsAndUpdate(sectionsToUpdate);
      const parsed = await response.json();
      const sections = parsed?.sections && typeof parsed.sections === 'object' ? parsed.sections : {};
      if (Object.keys(sections).length > 0 && sectionsToUpdate.size > 0) {
        updateCartSections(sections, sectionsToUpdate);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        return sections;
      }
    } catch (error) {
      console.warn('[Logistics Manager] Bundled section refresh failed, falling back to section API:', error);
    }
  }
  return await fetchCartSectionsAndUpdate(sectionsToUpdate);
}

/**
 * Core logistics add/remove logic (always run through the shared promise chain).
 * @param {any} cartData
 * @param {Set<string>|null} sectionsToUpdate
 * @returns {Promise<Record<string, string>>}
 */
async function manageLogisticsProductInternal(cartData, sectionsToUpdate = null) {
  // Collect sections if not provided
  if (!sectionsToUpdate) {
    sectionsToUpdate = collectSectionsToUpdate();
  }

  // Ensure we have cart data (needed for attribute and for CH/non-CH logic)
  if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
    cartData = await fetchFreshCartData();
    if (!cartData) return {};
  }

  // Always use latest cart snapshot before any add/remove decision
  const freshCartData = await fetchFreshCartData();
  if (freshCartData?.items) {
    cartData = freshCartData;
  }

  cartData = await normalizeLogisticsQuantity(cartData, sectionsToUpdate);
  let logisticsProductExists = cartHasLogistics(cartData.items);

  // Zubehör-only carts never get a logistics fee — check before discount/market side effects
  if (isCartZubehoerOnly(cartData.items)) {
    if (logisticsProductExists) {
      console.log('[Logistics Manager] Zubehör-only cart - removing logistics product');
      return await removeLogisticsProduct(cartData, sectionsToUpdate, { stripLogisticsSummary: true });
    }
    stripLogisticsSummaryFromAllContainers();
    await setAppliedDiscountCodeAttribute(cartData);
    return cartData.sections || {};
  }

  // Always keep applied_discount_code in sync (all markets, including CH) so checkout/market switch works
  await setAppliedDiscountCodeAttribute(cartData);

  // Skip logistics management for 'ch' market
  if (isLogisticsDisabled()) {
    if (logisticsProductExists) {
      console.log('[Logistics Manager] CH market detected - removing logistics product');
      return await removeLogisticsProduct(cartData, sectionsToUpdate);
    }
    return cartData?.sections || {};
  }

  const actualProductItems = getLogisticsEligibleProductItems(cartData.items);

  // If cart is empty, remove logistics product if it exists
  if (actualProductItems.length === 0) {
    if (logisticsProductExists) {
      return await removeLogisticsProduct(cartData, sectionsToUpdate);
    }
    return cartData.sections || {};
  }

  // Calculate cart total excluding service products
  const cartTotal = actualProductItems.reduce((total, item) => {
    return total + (item.final_line_price || item.line_price || 0);
  }, 0);

  console.log('[Logistics Manager] Cart total (excluding services):', cartTotal / 100);
  console.log('[Logistics Manager] Threshold:', MIN_CART_TOTAL_THRESHOLD / 100);

  // If a tracked discount code (e.g. FLEX45) is applied, always remove logistics product
  const appliedTrackedDiscount = getAppliedTrackedDiscountCode(cartData);
  if (appliedTrackedDiscount) {
    console.log('[Logistics Manager] Tracked discount applied, removing logistics product if present:', appliedTrackedDiscount);
    if (logisticsProductExists) {
      return await removeLogisticsProduct(cartData, sectionsToUpdate);
    }
    return cartData.sections || {};
  }

  // Remove logistics product if cart total is at or above threshold
  if (cartTotal >= MIN_CART_TOTAL_THRESHOLD && logisticsProductExists) {
    return await removeLogisticsProduct(cartData, sectionsToUpdate);
  }

  // Add logistics product if cart total is below threshold
  if (cartTotal < MIN_CART_TOTAL_THRESHOLD && !logisticsProductExists) {
    // Skip add if we just added moments ago (remove→re-add race / deferred retry)
    if (Date.now() - (logisticsLock.recentlyAddedAt || 0) < 2000) {
      const recentCart = await fetchFreshCartData();
      if (cartHasLogistics(recentCart?.items)) {
        console.log('[Logistics Manager] Skipping add — logistics was just added');
        clearPendingLogisticsCheck();
        if (sectionsToUpdate && sectionsToUpdate.size > 0) {
          await fetchCartSectionsViaBundledRendering(recentCart, sectionsToUpdate);
        }
        return recentCart?.sections || {};
      }
    }

    // Re-check immediately before add — queued retries often race the first add
    const preAddCart = await fetchFreshCartData();
    if (cartHasLogistics(preAddCart?.items)) {
      console.log('[Logistics Manager] Logistics already in cart before add — skipping duplicate, refreshing UI');
      clearPendingLogisticsCheck();
      const normalized = await normalizeLogisticsQuantity(preAddCart, sectionsToUpdate);
      if (sectionsToUpdate && sectionsToUpdate.size > 0) {
        await fetchCartSectionsViaBundledRendering(normalized, sectionsToUpdate);
      }
      return normalized?.sections || {};
    }
    const addedSections = await addLogisticsProduct(sectionsToUpdate);
    // Successful add — cancel any queued retry so remove→re-add cannot double-add
    clearPendingLogisticsCheck();
    return addedSections;
  }

  // No changes needed, return original sections if available
  return cartData.sections || {};
}

/**
 * Manages logistics service product in cart.
 * Serialized via a window-level promise chain so duplicate module instances cannot double-add.
 * @param {any} cartData - Cart data from API response
 * @param {Set<string>} sectionsToUpdate - Sections that need updating
 * @returns {Promise<Record<string, string>>} Updated section HTML
 */
export function manageLogisticsProduct(cartData, sectionsToUpdate = null) {
  const lock = getLogisticsLock();
  const g = typeof globalThis !== 'undefined' ? globalThis : window;

  // Global managing flag (not just lock.managing) so duplicate module instances share it
  if (g.__flexcubeLogisticsManaging || lock.managing) {
    console.warn('[Logistics Manager] Another logistics management operation is in progress, queuing retry');
    schedulePendingLogisticsCheck(350);
    return Promise.resolve(cartData?.sections || {});
  }
  g.__flexcubeLogisticsManaging = true;
  lock.managing = true;

  if (!g.__flexcubeLogisticsManageChain) {
    g.__flexcubeLogisticsManageChain = Promise.resolve();
  }

  const run = g.__flexcubeLogisticsManageChain.then(async () => {
    try {
      return await manageLogisticsProductInternal(cartData, sectionsToUpdate);
    } finally {
      g.__flexcubeLogisticsManaging = false;
      lock.managing = false;
      if (lock.pending) {
        schedulePendingLogisticsCheck(350);
      }
    }
  });

  g.__flexcubeLogisticsManageChain = run.catch((error) => {
    console.error('[Logistics Manager] Queued logistics run failed:', error);
    g.__flexcubeLogisticsManaging = false;
    lock.managing = false;
  });

  return run;
}

/**
 * Sets logistics line quantity via /cart/update.js.
 * Serialized on a global mutation chain with a fresh cart check so parallel callers
 * cannot both "add" when the line is missing (Shopify can still end up at qty 2).
 * @param {number} quantity
 * @param {Set<string>} sectionsToUpdate
 * @returns {Promise<any>}
 */
function setLogisticsQuantity(quantity, sectionsToUpdate) {
  // Keep mutation chain on globalThis directly so duplicate module instances cannot race
  const g = typeof globalThis !== 'undefined' ? globalThis : window;
  if (!g.__flexcubeLogisticsMutationChain) {
    g.__flexcubeLogisticsMutationChain = Promise.resolve();
  }

  const run = g.__flexcubeLogisticsMutationChain.then(async () => {
    const fresh = await fetchFreshCartData();
    const lines = getLogisticsLines(fresh?.items);
    const currentQty = lines.reduce((sum, item) => sum + (item.quantity || 0), 0);

    if (quantity > 0) {
      if (currentQty >= 1) {
        console.log('[Logistics Manager] Logistics already in cart — skipping write, normalizing if needed');
        if (currentQty > 1 || lines.length > 1) {
          return normalizeLogisticsQuantity(fresh, sectionsToUpdate);
        }
        return fresh || {};
      }
    } else if (currentQty === 0) {
      console.log('[Logistics Manager] Logistics already absent — skipping remove write');
      return fresh || {};
    }

    const cartUpdateUrl = typeof theme !== 'undefined' && theme.routes?.cart_update_url
      ? theme.routes.cart_update_url
      : '/cart/update.js';
    const body = JSON.stringify({
      updates: { [LOGISTICS_VARIANT_ID]: quantity },
      sections: sectionsToUpdate ? Array.from(sectionsToUpdate).join(',') : '',
      sections_url: window.location.pathname,
    });
    const response = await fetch(cartUpdateUrl, fetchConfig('json', { body }));
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to set logistics quantity to ${quantity}`);
    }
    return response.json();
  });

  g.__flexcubeLogisticsMutationChain = run.catch((error) => {
    console.error('[Logistics Manager] Logistics cart mutation failed:', error);
  });

  return run;
}

/**
 * Adds the logistics service product to cart (idempotent: forces qty to 1).
 * @param {Set<string>} sectionsToUpdate - Sections to update
 * @returns {Promise<Record<string, string>>} Updated section HTML
 */
async function addLogisticsProduct(sectionsToUpdate) {
  console.log('[Logistics Manager] Adding logistics service product to cart');

  const result = await setLogisticsQuantity(1, sectionsToUpdate);
  console.log('[Logistics Manager] Logistics service product added successfully');
  logisticsLock.recentlyAddedAt = Date.now();
  clearPendingLogisticsCheck();

  // Extra safety if an old /cart/add.js path somehow raced
  let sections = result.sections || {};
  const postAddCart = await fetchFreshCartData();
  if (postAddCart) {
    const normalized = await normalizeLogisticsQuantity(postAddCart, sectionsToUpdate);
    if (normalized?.sections) sections = normalized.sections;
  }

  // cart.js skips section morph for source=logistics-service, so we must update DOM here
  if (sections && Object.keys(sections).length > 0 && sectionsToUpdate && sectionsToUpdate.size > 0) {
    updateCartSections(sections, sectionsToUpdate);
  } else if (sectionsToUpdate && sectionsToUpdate.size > 0) {
    await fetchCartSectionsViaBundledRendering(postAddCart || result, sectionsToUpdate);
  }
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));

  if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
    setTimeout(async () => {
      try {
        const cartJson = await fetchFreshCartData();
        if (cartJson) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'logistics-service',
            cart: { ...cartJson, sections }
          });
        }
      } catch (error) {
        console.warn('[Logistics Manager] Could not publish cart update event:', error);
      }
    }, 100);
  }

  return sections;
}

/**
 * Removes the logistics service product from cart
 * @param {any} cartData - Current cart object
 * @param {Set<string>} sectionsToUpdate - Sections to update
 * @returns {Promise<Record<string, string>>} Updated section HTML
 */
async function removeLogisticsProduct(cartData, sectionsToUpdate, options = {}) {
  console.log('[Logistics Manager] Removing logistics service product from cart');
  const stripLogisticsSummary = !!options.stripLogisticsSummary;

  // Fresh check — avoid stale line-key removes when two managers race
  const fresh = await fetchFreshCartData();
  if (fresh?.items) cartData = fresh;

  if (!cartHasLogistics(cartData?.items)) {
    console.log('[Logistics Manager] Logistics product not found in cart - already removed');
    return {};
  }

  let parsedResult;
  try {
    // Idempotent: setting qty 0 twice is safe (unlike /cart/change.js with a stale line key)
    parsedResult = await setLogisticsQuantity(0, sectionsToUpdate);
  } catch (error) {
    const errorText = String(error?.message || error || '');
    if (/gültigen ID|valid id|line|not found/i.test(errorText)) {
      console.warn('[Logistics Manager] Logistics already removed — ignoring');
      return {};
    }
    console.error('[Logistics Manager] Failed to remove logistics service product:', errorText);
    throw new Error('Failed to remove logistics service product');
  }

  console.log('[Logistics Manager] Logistics service product removed successfully');
  logisticsLock.recentlyAddedAt = 0;
  clearPendingLogisticsCheck();

  if (parsedResult.sections && Object.keys(parsedResult.sections).length > 0 && sectionsToUpdate && sectionsToUpdate.size > 0) {
    updateCartSections({ sections: parsedResult.sections, stripLogisticsSummary }, sectionsToUpdate);
  } else if (sectionsToUpdate && sectionsToUpdate.size > 0) {
    await fetchCartSectionsViaBundledRendering(parsedResult, sectionsToUpdate);
  }
  if (stripLogisticsSummary) {
    stripLogisticsSummaryFromAllContainers();
    [400, 750, 1100].forEach((ms) => {
      setTimeout(() => stripLogisticsSummaryFromAllContainers(), ms);
    });
  }
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));

  if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
    setTimeout(async () => {
      try {
        const cartJson = await fetchFreshCartData();
        if (!cartJson) return;
        import('./service-products-updater.js').then(({ updateServiceProductsSummary }) => {
          updateServiceProductsSummary(cartJson);
        }).catch(() => {});
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'logistics-service',
          cart: { ...cartJson, sections: parsedResult.sections || {} }
        });
      } catch (error) {
        console.warn('[Logistics Manager] Could not publish cart update event:', error);
      }
    }, 100);
  }

  return { sections: parsedResult.sections || {}, stripLogisticsSummary };
}

// Storage key for persisting market state across page reloads
const MARKET_STATE_STORAGE_KEY = 'logistics_manager_market_state';

// Track previous market state to detect changes
let previousMarketState = null;
// Track previous pathname to detect URL changes
let previousPathname = null;
// Track previous theme.localization values to detect changes even if pathname doesn't change
let previousThemeCountryCode = null;
let previousThemeMarketHandle = null;

/**
 * Saves current market state to localStorage
 */
function saveMarketStateToStorage() {
  try {
    const currentCountryCode = typeof theme !== 'undefined' && theme.localization?.country?.iso_code;
    const currentMarketHandle = typeof theme !== 'undefined' && theme.localization?.market?.handle;
    const currentState = getCurrentMarketState();
    const currentPathname = window.location.pathname;
    
    const stateToSave = {
      marketState: currentState,
      countryCode: currentCountryCode,
      marketHandle: currentMarketHandle,
      pathname: currentPathname,
      timestamp: Date.now()
    };
    
    localStorage.setItem(MARKET_STATE_STORAGE_KEY, JSON.stringify(stateToSave));
    console.log('[Logistics Manager] Saved market state to storage:', stateToSave);
  } catch (error) {
    console.warn('[Logistics Manager] Could not save market state to storage:', error);
  }
}

/**
 * Loads previous market state from localStorage
 */
function loadMarketStateFromStorage() {
  try {
    const saved = localStorage.getItem(MARKET_STATE_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('[Logistics Manager] Loaded market state from storage:', parsed);
      return parsed;
    }
  } catch (error) {
    console.warn('[Logistics Manager] Could not load market state from storage:', error);
  }
  return null;
}

/**
 * Gets current market state for comparison
 * @returns {boolean} True if CH market is currently active
 */
function getCurrentMarketState() {
  return isLogisticsDisabled();
}

/**
 * Handles market change detection and manages logistics product accordingly
 */
async function handleMarketChange() {
  // Prevent concurrent execution
  if (logisticsLock.handlingMarketChange) {
    console.log('[Logistics Manager] Market change handling already in progress, skipping');
    return;
  }
  
  logisticsLock.handlingMarketChange = true;
  
  try {
    // Small delay to ensure theme.localization is updated after navigation
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const currentMarketState = getCurrentMarketState();
    const pathname = window.location.pathname;
    
    // Get current theme.localization values
    const themeMarketHandle = typeof theme !== 'undefined' && theme.localization?.market?.handle;
    const themeCountryCode = typeof theme !== 'undefined' && theme.localization?.country?.iso_code;
    
    // Check if pathname changed (indicates potential market switch)
    const pathnameChanged = previousPathname !== null && pathname !== previousPathname;
    
    // Check if theme.localization values changed (indicates market switch even if pathname doesn't change)
    // Use String() to handle null/undefined comparisons properly
    const countryCodeChanged = previousThemeCountryCode !== null && String(themeCountryCode) !== String(previousThemeCountryCode);
    const marketHandleChanged = previousThemeMarketHandle !== null && String(themeMarketHandle) !== String(previousThemeMarketHandle);
    const themeLocalizationChanged = countryCodeChanged || marketHandleChanged;
    
    // Force check if pathname changed, theme.localization changed, market state changed, or if this is initial load
    const shouldProcess = pathnameChanged || 
                          themeLocalizationChanged ||
                          previousMarketState === null || 
                          previousMarketState !== currentMarketState;
    
    if (!shouldProcess) {
      // Update tracking values even if no change
      previousPathname = pathname;
      previousThemeCountryCode = themeCountryCode;
      previousThemeMarketHandle = themeMarketHandle;
      // Still save to storage even if no change (to update timestamp and ensure consistency)
      saveMarketStateToStorage();
      return;
    }
    
    // Update state immediately to prevent polling from triggering again
    const wasChMarket = previousMarketState === true;
    const isChMarket = currentMarketState === true;
    
    // Update previous state immediately (before async operations) to prevent duplicate triggers
    previousMarketState = currentMarketState;
    previousPathname = pathname;
    previousThemeCountryCode = themeCountryCode;
    previousThemeMarketHandle = themeMarketHandle;
    
    console.log('[Logistics Manager] Processing market change:', {
      wasChMarket,
      isChMarket,
      switchingToCH: isChMarket && !wasChMarket,
      switchingFromCH: !isChMarket && wasChMarket
    });
    
    // Fetch current cart to check logistics product
    const cartUrl = typeof theme !== 'undefined' && theme.routes?.cart_url ? theme.routes.cart_url : '/cart.js';
    const response = await fetch(cartUrl, fetchConfig('json', { method: 'GET' }));
    if (!response.ok) {
      console.error('[Logistics Manager] Failed to fetch cart for market change');
      return;
    }
    
    const cartData = await response.json();
    const sectionsToUpdate = collectSectionsToUpdate();
    let updatedSections = {};
    
    // If switching TO CH market, remove logistics product
    if (isChMarket && !wasChMarket) {
      const logisticsExists = cartData.items?.some((item) =>
        String(item.variant_id) === LOGISTICS_VARIANT_ID
      );
      
      if (logisticsExists) {
        console.log('[Logistics Manager] Removing logistics product for CH market');
        // Fetch fresh cart data before removing to ensure we have the latest item keys
        const freshCartResponse = await fetch(cartUrl, fetchConfig('json', { method: 'GET' }));
        if (freshCartResponse.ok) {
          const freshCartData = await freshCartResponse.json();
          await removeLogisticsProduct(freshCartData, sectionsToUpdate);
        }
      }
    }
    // If switching FROM CH market, check if logistics should be added
    else if (!isChMarket && wasChMarket) {
      console.log('[Logistics Manager] Non-CH market active, checking if logistics should be added (threshold: 950)');
      await manageLogisticsProduct(cartData, sectionsToUpdate);
    }
    
    // Save to storage so it persists across page reloads
    saveMarketStateToStorage();
    
  } catch (error) {
    console.error('[Logistics Manager] Error handling market change:', error);
  } finally {
    logisticsLock.handlingMarketChange = false;
  }
}

/**
 * Single cart-update handler shared across duplicate module instances.
 * Marked so we can strip duplicates out of theme.pubsub.subscribers.
 */
async function handleLogisticsCartUpdate(event) {
  if (event?.source === 'logistics-service') return;
  if (event?.source === 'assembly-service') return;

  try {
    await manageLogisticsProduct(event?.cart);

    const freshCart = await fetchFreshCartData();
    if (freshCart && isCartZubehoerOnly(freshCart.items)) {
      stripLogisticsSummaryFromAllContainers();
      [350, 800].forEach((ms) => {
        setTimeout(() => stripLogisticsSummaryFromAllContainers(), ms);
      });
      import('./service-products-updater.js').then(({ updateServiceProductsSummary }) => {
        updateServiceProductsSummary(freshCart);
      }).catch(() => {});
    }

    if (event?.source === 'cart-discount') {
      const sectionsToUpdate = collectSectionsToUpdate();
      const sections = await fetchCartSectionsViaBundledRendering(event.cart, sectionsToUpdate);
      if (Object.keys(sections).length > 0 && typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        try {
          const base = (theme.routes?.cart_url) ? theme.routes.cart_url : '/cart';
          const cartUrl = base.endsWith('.js') ? base : base + '.js';
          const cartRes = await fetch(cartUrl, fetchConfig('json', { method: 'GET' }));
          if (cartRes.ok) {
            const cartJson = await cartRes.json();
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'logistics-service',
              cart: cartJson
            });
          }
        } catch (e) {
          console.warn('[Logistics Manager] Could not publish cart after discount refresh:', e);
        }
      }
    }
    await handleMarketChange();
  } catch (error) {
    console.error('[Logistics Manager] Error managing logistics product:', error);
  }
}
handleLogisticsCartUpdate.__isFlexcubeLogisticsHandler = true;

/**
 * Ensures exactly one logistics cartUpdate subscriber exists (safe if init runs twice).
 */
function ensureUniqueCartSubscription() {
  if (typeof theme === 'undefined' || !theme.pubsub || !theme.pubsub.PUB_SUB_EVENTS) {
    return false;
  }
  const lock = getLogisticsLock();
  const eventName = theme.pubsub.PUB_SUB_EVENTS.cartUpdate;

  if (typeof lock.unsubscribeCart === 'function') {
    try { lock.unsubscribeCart(); } catch (_) { /* ignore */ }
    lock.unsubscribeCart = null;
  }

  // Strip any leftover handlers from duplicate module boots
  if (Array.isArray(theme.pubsub.subscribers?.[eventName])) {
    theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[eventName].filter(
      (cb) => !cb?.__isFlexcubeLogisticsHandler
    );
  }

  lock.unsubscribeCart = theme.pubsub.subscribe(eventName, handleLogisticsCartUpdate);
  return true;
}

function teardownLogisticsDomListeners() {
  const g = typeof globalThis !== 'undefined' ? globalThis : window;
  const lock = getLogisticsLock();
  const onFocus = g.__flexcubeLogisticsOnFocus || lock.onFocus;
  const onVisibility = g.__flexcubeLogisticsOnVisibility || lock.onVisibility;
  if (onFocus) window.removeEventListener('focus', onFocus);
  if (onVisibility) document.removeEventListener('visibilitychange', onVisibility);
  g.__flexcubeLogisticsOnFocus = null;
  g.__flexcubeLogisticsOnVisibility = null;
  lock.onFocus = null;
  lock.onVisibility = null;
  if (lock.pollInterval) {
    clearInterval(lock.pollInterval);
    lock.pollInterval = null;
  }
  if (lock.slowPollInterval) {
    clearInterval(lock.slowPollInterval);
    lock.slowPollInterval = null;
  }
}

// Subscribe to cart updates to automatically manage logistics product
function initializeLogisticsManager() {
  const lock = getLogisticsLock();

  if (typeof theme === 'undefined' || !theme.pubsub || !theme.pubsub.PUB_SUB_EVENTS) {
    setTimeout(initializeLogisticsManager, 100);
    return;
  }

  // Always re-bind a single cart handler (dedupes even if a second module instance boots)
  ensureUniqueCartSubscription();

  // Full one-time setup (market observers, history hooks, etc.)
  if (lock.subscribed) {
    console.log('[Logistics Manager] Already initialized — cart handler refreshed only');
    return;
  }
  lock.subscribed = true;
  
  // Initialize previous market state (wait for theme.localization to be available)
  const initMarketState = () => {
    // Check if theme.localization is available
    if (typeof theme !== 'undefined' && theme.localization) {
      // Get current market state
      const currentMarketState = getCurrentMarketState();
      const currentPathname = window.location.pathname;
      const currentCountryCode = theme.localization?.country?.iso_code;
      const currentMarketHandle = theme.localization?.market?.handle;
      
      // Load previous state from storage (persists across page reloads)
      const savedState = loadMarketStateFromStorage();
      
      if (savedState) {
        // Use saved state as previous state to detect changes across page reloads
        previousMarketState = savedState.marketState;
        previousPathname = savedState.pathname;
        previousThemeCountryCode = savedState.countryCode;
        previousThemeMarketHandle = savedState.marketHandle;
        
        console.log('[Logistics Manager] Initialized with saved previous state:', {
          saved: savedState,
          current: {
            state: currentMarketState,
            pathname: currentPathname,
            countryCode: currentCountryCode,
            marketHandle: currentMarketHandle
          }
        });
        
        // Check if market changed (compare saved vs current)
        const marketChanged = savedState.marketState !== currentMarketState ||
                              savedState.countryCode !== currentCountryCode ||
                              savedState.marketHandle !== currentMarketHandle ||
                              savedState.pathname !== currentPathname;
        
        if (marketChanged) {
          console.log('[Logistics Manager] ✓ Market change detected on page load!');
          console.log('[Logistics Manager] Saved (previous) state:', {
            state: previousMarketState,
            countryCode: previousThemeCountryCode,
            marketHandle: previousThemeMarketHandle,
            pathname: previousPathname
          });
          console.log('[Logistics Manager] Current state:', {
            state: currentMarketState,
            countryCode: currentCountryCode,
            marketHandle: currentMarketHandle,
            pathname: currentPathname
          });
          // Keep previousMarketState from saved state - DON'T update it yet
          // handleMarketChange() will be called and will process the change, then update previousMarketState
        } else {
          // No change detected, set previous to current for this session
          previousMarketState = currentMarketState;
          previousPathname = currentPathname;
          previousThemeCountryCode = currentCountryCode;
          previousThemeMarketHandle = currentMarketHandle;
          console.log('[Logistics Manager] No market change detected, using current state as previous');
          // Save current state since it hasn't changed
          saveMarketStateToStorage();
        }
      } else {
        // No saved state (first load or storage cleared)
        previousMarketState = currentMarketState;
        previousPathname = currentPathname;
        previousThemeCountryCode = currentCountryCode;
        previousThemeMarketHandle = currentMarketHandle;
        
        console.log('[Logistics Manager] Initial market state (no saved state):', {
          state: previousMarketState,
          pathname: previousPathname,
          countryCode: previousThemeCountryCode,
          marketHandle: previousThemeMarketHandle
        });
      }
      
      // Don't save yet - wait for handleMarketChange() to process and save after processing
      // This prevents false positives where saved state equals current state
    } else {
      // Retry if theme.localization not ready yet
      setTimeout(initMarketState, 100);
      return;
    }
  };
  
  initMarketState();
  
  // Listen for URL changes (market switches often change URL)
  let lastUrl = window.location.href;
  let lastPathname = window.location.pathname;
  
  const checkUrlChange = () => {
    const currentUrl = window.location.href;
    const currentPathname = window.location.pathname;
    
    // Check if pathname changed (more reliable indicator of market switch)
    if (currentPathname !== lastPathname || currentUrl !== lastUrl) {
      console.log('[Logistics Manager] URL/Pathname changed:', {
        oldPathname: lastPathname,
        newPathname: currentPathname,
        oldUrl: lastUrl,
        newUrl: currentUrl
      });
      
      lastUrl = currentUrl;
      lastPathname = currentPathname;
      
      // Reset previous state, pathname, and theme.localization to force check (market might have changed)
      previousMarketState = null;
      previousPathname = null;
      previousThemeCountryCode = null;
      previousThemeMarketHandle = null;
      
      // Delay to allow theme.localization to update
      setTimeout(() => handleMarketChange(), 500);
    }
  };
  
  const urlObserver = new MutationObserver(checkUrlChange);
  
  if (document.body) {
    urlObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-market', 'class']
    });
  }
  
  urlObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-market', 'lang', 'dir']
  });
  
  // Override pushState/replaceState once only
  const gNav = typeof globalThis !== 'undefined' ? globalThis : window;
  if (!gNav.__flexcubeLogisticsHistoryPatched) {
    gNav.__flexcubeLogisticsHistoryPatched = true;
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      console.log('[Logistics Manager] pushState called');
      previousMarketState = null;
      previousPathname = null;
      previousThemeCountryCode = null;
      previousThemeMarketHandle = null;
      setTimeout(() => handleMarketChange(), 500);
    };

    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      console.log('[Logistics Manager] replaceState called');
      previousMarketState = null;
      previousPathname = null;
      previousThemeCountryCode = null;
      previousThemeMarketHandle = null;
      setTimeout(() => handleMarketChange(), 500);
    };

    window.addEventListener('popstate', () => {
      console.log('[Logistics Manager] Popstate event');
      previousMarketState = null;
      previousPathname = null;
      previousThemeCountryCode = null;
      previousThemeMarketHandle = null;
      setTimeout(() => handleMarketChange(), 500);
    });
  }
  
  // Also listen for market attribute changes directly
  const marketObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-market') {
        const newMarketValue = mutation.target.getAttribute('data-market');
        console.log('[Logistics Manager] Market attribute changed:', {
          target: mutation.target.tagName,
          oldValue: mutation.oldValue,
          newValue: newMarketValue
        });
        previousMarketState = null; // Reset to force check
        previousPathname = null;
        previousThemeCountryCode = null;
        previousThemeMarketHandle = null;
        setTimeout(() => handleMarketChange(), 500);
      }
    });
  });
  
  if (document.body) {
    marketObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-market']
    });
  }
  
  marketObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-market']
  });
  
  // Check immediately on page load (after theme.localization is ready)
  setTimeout(() => {
    // Ensure tracking values are initialized before checking
    if (previousMarketState === null || previousMarketState === undefined) {
      previousMarketState = getCurrentMarketState();
    }
    if (previousPathname === null || previousPathname === undefined) {
      previousPathname = window.location.pathname;
    }
    if (previousThemeCountryCode === null || previousThemeCountryCode === undefined) {
      previousThemeCountryCode = typeof theme !== 'undefined' && theme.localization?.country?.iso_code;
    }
    if (previousThemeMarketHandle === null || previousThemeMarketHandle === undefined) {
      previousThemeMarketHandle = typeof theme !== 'undefined' && theme.localization?.market?.handle;
    }
    handleMarketChange();
  }, 1500);
  
  // Poll theme.localization periodically to catch changes that don't trigger events
  teardownLogisticsDomListeners();
  let pollCount = 0;
  const maxFrequentPolls = 15;

  lock.pollInterval = setInterval(() => {
    if (logisticsLock.handlingMarketChange) return;

    pollCount++;

    const currentCountryCode = typeof theme !== 'undefined' && theme.localization?.country?.iso_code || null;
    const currentMarketHandle = typeof theme !== 'undefined' && theme.localization?.market?.handle || null;
    const currentPathname = window.location.pathname;

    const countryCodeChanged = String(currentCountryCode) !== String(previousThemeCountryCode);
    const marketHandleChanged = String(currentMarketHandle) !== String(previousThemeMarketHandle);
    const pathnameChanged = currentPathname !== previousPathname;
    const marketStateChanged = getCurrentMarketState() !== previousMarketState;

    if (countryCodeChanged || marketHandleChanged || pathnameChanged || marketStateChanged) {
      handleMarketChange();
    }

    if (pollCount >= maxFrequentPolls) {
      clearInterval(lock.pollInterval);
      lock.pollInterval = null;
      lock.slowPollInterval = setInterval(() => {
        if (logisticsLock.handlingMarketChange) return;

        const cc = typeof theme !== 'undefined' && theme.localization?.country?.iso_code || null;
        const mh = typeof theme !== 'undefined' && theme.localization?.market?.handle || null;
        const pn = window.location.pathname;

        if (
          String(cc) !== String(previousThemeCountryCode) ||
          String(mh) !== String(previousThemeMarketHandle) ||
          pn !== previousPathname ||
          getCurrentMarketState() !== previousMarketState
        ) {
          handleMarketChange();
        }
      }, 3000);
    }
  }, 1000);

  const gDom = typeof globalThis !== 'undefined' ? globalThis : window;
  lock.onFocus = () => {
    console.log('[Logistics Manager] Window focus event');
    setTimeout(() => handleMarketChange(), 300);
  };
  gDom.__flexcubeLogisticsOnFocus = lock.onFocus;
  window.addEventListener('focus', lock.onFocus);

  lock.onVisibility = () => {
    if (!document.hidden) {
      console.log('[Logistics Manager] Page became visible');
      previousMarketState = null;
      previousPathname = null;
      previousThemeCountryCode = null;
      previousThemeMarketHandle = null;
      setTimeout(() => handleMarketChange(), 500);
    }
  };
  gDom.__flexcubeLogisticsOnVisibility = lock.onVisibility;
  document.addEventListener('visibilitychange', lock.onVisibility);

  console.log('[Logistics Manager] Initialized and subscribed to cart updates and market changes');
}

// Initialize once (guard against duplicate module evaluation / double script tags)
function bootLogisticsManager() {
  const lock = getLogisticsLock();
  if (lock.initialized || lock.subscribed) {
    console.log('[Logistics Manager] Already initialized — skipping duplicate boot');
    return;
  }
  lock.initialized = true;
  initializeLogisticsManager();
}

// Absolute guard: only the first module evaluation may schedule boot
const g = typeof globalThis !== 'undefined' ? globalThis : window;
if (!g.__flexcubeLogisticsBootScheduled) {
  g.__flexcubeLogisticsBootScheduled = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootLogisticsManager);
  } else {
    bootLogisticsManager();
  }
} else {
  console.log('[Logistics Manager] Boot already scheduled by another module instance — skipping');
}
